import React, { useState } from "react";
import {
  StyleSheet,
  View,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
  type StyleProp,
  type ViewStyle,
} from "react-native";
import { ThemedText } from "../ThemedText";
import { ThemedChip } from "../ThemedChip";
import { ThemedSegmentedControl } from "../ThemedSegmentedControl";
import { ShareModel, Contact, ContactModel, GroupModel } from "common247";
import { Ionicons } from "@expo/vector-icons";
import { useThemeColor } from "../../hooks/useThemeColor";
import { ShareRecipientSelector } from "./ShareRecipientSelector";
import type { IPopoverAnchor } from "@/utils/popover-positioning";

/**
 * Props for the SharingControl component.
 */
export interface ISharingControlProps {
  shares: ShareModel[];
  contacts?: (Contact | ContactModel)[];
  groups?: GroupModel[];
  isLoading?: boolean;
  canShare?: boolean;
  canUnshare?: boolean;
  readOnly?: boolean;
  compact?: boolean;
  style?: StyleProp<ViewStyle>;
  onShare: (targetId: string, targetType: "contact" | "group", role: "viewer" | "editor") => void;
  onUnshare: (shareId: string) => void;
  testID?: string;
}

/**
 * Shared scroll properties contract for target lists:
 * showsVerticalScrollIndicator={true}
 * persistentScrollbar={true}
 * nestedScrollEnabled={true}
 */
export const SHARING_LIST_SCROLL_PROPS = {
  showsVerticalScrollIndicator: true,
  persistentScrollbar: true,
  nestedScrollEnabled: true,
};

/**
 * Resolves recipient display name from share metadata or target lookups.
 */
function getRecipientDisplayName(
  share: ShareModel,
  contacts: (Contact | ContactModel)[] = [],
  groups: GroupModel[] = []
): string {
  if (share.targetDisplayName && share.targetDisplayName.trim()) {
    return share.targetDisplayName;
  }
  if (share.targetType === "contact") {
    const contact = contacts.find((c) => c.id === share.targetId);
    if (contact) {
      return contact.displayName || contact.email || share.targetId;
    }
  } else if (share.targetType === "group") {
    const group = groups.find((g) => g.id === share.targetId);
    if (group) {
      return group.name || share.targetId;
    }
  }
  return share.targetId;
}

/**
 * Standardized Sharing Control matching the TagListStrip UX pattern.
 * Renders an unbordered inline flex-wrap strip of shared recipient pills
 * with a compact "+ Share" pill button opening a contextual ShareSelectorModal.
 */
export function SharingControl({
  shares = [],
  contacts = [],
  groups = [],
  isLoading = false,
  canShare = false,
  canUnshare = false,
  readOnly = false,
  compact = false,
  style,
  onShare,
  onUnshare,
  testID,
}: ISharingControlProps) {
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [shareAnchor, setShareAnchor] = useState<IPopoverAnchor | null>(null);

  const borderColor = useThemeColor({}, "border");
  const tintColor = useThemeColor({}, "tint");
  const cardBg = useThemeColor({}, "card");
  const textMuted = useThemeColor({}, "textMuted");
  const textDefault = useThemeColor({}, "text");
  const badgeBg = useThemeColor({}, "buttonSecondary");
  const isDark = cardBg === "#131520";

  const isReadOnlyMode = readOnly || !canShare;

  if (isLoading) {
    return (
      <View style={[styles.container, style]}>
        <ActivityIndicator style={{ marginVertical: 4 }} color={tintColor} />
      </View>
    );
  }

  // If no shares and user cannot edit/share, don't render an empty block
  if (shares.length === 0 && isReadOnlyMode) {
    return null;
  }

  return (
    <View
      style={[styles.container, compact && styles.containerCompact, style]}
      {...(Platform.OS === "web"
        ? {
            onClick: (e: any) => e?.stopPropagation?.(),
          }
        : {})}
      testID={testID}
    >
      <View style={styles.shareFlexRow}>
        {shares.map((share, idx) => {
          const name = getRecipientDisplayName(share, contacts, groups);
          const isGroup = share.targetType === "group";
          const currentRole: "viewer" | "editor" = share.role === "editor" ? "editor" : "viewer";

          return (
            <ThemedChip
              key={share.id || `${share.targetId}-${idx}`}
              entityType={isGroup ? "group" : "contact"}
              id={share.targetId}
              label={name}
              secondaryLabel={isReadOnlyMode ? (currentRole === "editor" ? "Editor" : "Viewer") : undefined}
              compact={compact}
              canRemove={canUnshare && !readOnly}
              onRemove={canUnshare && !readOnly ? () => onUnshare(share.id!) : undefined}
              removeTestID={`share-remove-${share.id || idx}`}
              testID={`share-chip-${share.id || idx}`}
              readOnly={isReadOnlyMode}
            >
              {!isReadOnlyMode ? (
                <ThemedSegmentedControl<"viewer" | "editor">
                  size="small"
                  value={currentRole}
                  onChange={(newRole) => onShare(share.targetId, share.targetType, newRole)}
                  options={[
                    { label: "Viewer", value: "viewer" },
                    { label: "Editor", value: "editor" },
                  ]}
                  containerStyle={styles.chipSegmentedControl}
                />
              ) : null}
            </ThemedChip>
          );
        })}

        {/* Compact + Share Button matching + Tag in TagListStrip */}
        {canShare && !readOnly && (
          <TouchableOpacity
            activeOpacity={0.7}
            onPress={(e: any) => {
              const x = e?.nativeEvent?.pageX ?? e?.nativeEvent?.clientX ?? 0;
              const y = e?.nativeEvent?.pageY ?? e?.nativeEvent?.clientY ?? 0;
              setShareAnchor(x || y ? { x, y } : null);
              e?.stopPropagation?.();
              setIsPickerOpen(true);
            }}
            hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}
            style={[
              styles.addShareButton,
              compact && styles.addShareButtonCompact,
              { backgroundColor: badgeBg, borderColor },
            ]}
            testID="sharing-control-add-share-btn"
          >
            <Ionicons name="add" size={compact ? 10 : 11} color={tintColor} />
            <ThemedText
              style={[
                styles.addShareText,
                compact && styles.addShareTextCompact,
                { color: tintColor },
              ]}
            >
              Share
            </ThemedText>
          </TouchableOpacity>
        )}
      </View>

      {/* Popover Share Recipient Selector */}
      {isPickerOpen && (
        <ShareRecipientSelector
          visible={isPickerOpen}
          onClose={() => setIsPickerOpen(false)}
          shares={shares}
          contacts={contacts}
          groups={groups}
          anchorPosition={shareAnchor}
          onShare={onShare}
          testID="sharing-control-modal"
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    marginVertical: 1,
    width: "100%",
  },
  containerCompact: {
    marginVertical: 0,
  },
  shareFlexRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 6,
  },
  sharePill: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 2,
    paddingHorizontal: 8,
    borderWidth: 1,
    borderRadius: 14,
    gap: 5,
    marginVertical: 1,
  },
  sharePillCompact: {
    paddingVertical: 1,
    paddingHorizontal: 6,
    borderRadius: 10,
    gap: 4,
    marginVertical: 0,
  },
  pillIcon: {
    marginRight: 1,
  },
  pillNameText: {
    fontSize: 12,
    fontWeight: "600",
    maxWidth: 140,
  },
  pillNameTextCompact: {
    fontSize: 11,
    maxWidth: 100,
  },
  chipSegmentedControl: {
    marginHorizontal: 2,
    transform: [{ scale: 0.9 }],
  },
  chipRemoveBtn: {
    paddingHorizontal: 3,
    paddingVertical: 1,
    marginLeft: 2,
  },
  chipRemoveText: {
    fontSize: 12,
    fontWeight: "700",
  },
  roleBadge: {
    paddingVertical: 1,
    paddingHorizontal: 4,
    borderRadius: 0,
  },
  roleBadgeEditor: {
    backgroundColor: "#FDFAD8",
    borderWidth: 1,
    borderColor: "#CAD23C",
  },
  roleBadgeText: {
    fontSize: 9.5,
    fontWeight: "700",
    textTransform: "uppercase",
    letterSpacing: 0.3,
  },
  roleBadgeTextEditor: {
    color: "#556B2F",
  },

  // + Share Button matching + Tag in TagListStrip
  addShareButton: {
    height: 19,
    maxHeight: 19,
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 5,
    paddingVertical: 0,
    borderRadius: 4,
    borderWidth: 1,
    marginVertical: 1,
    marginRight: 6,
    gap: 2.5,
  },
  addShareButtonCompact: {
    height: 17,
    maxHeight: 17,
    paddingHorizontal: 4,
    paddingVertical: 0,
    borderRadius: 3,
    marginVertical: 0,
    marginRight: 4,
    gap: 2,
  },
  addShareText: {
    fontSize: 11,
    lineHeight: 15,
    fontWeight: "600",
  },
  addShareTextCompact: {
    fontSize: 10,
    lineHeight: 13,
  },

  // Preserved style definitions for scroll and overflow testing
  scrollList: {
    maxHeight: 200,
    minWidth: 0,
    ...Platform.select({
      web: {
        overflowY: "auto",
        scrollbarWidth: "thin",
      },
    }),
  },
  pickerTray: {
    maxHeight: 220,
    minWidth: 0,
  },
});
